import React, { createContext, useContext, useState, useCallback } from 'react';
import { ContactForm, ContactFormConfig, FormField } from '../types/ContactForm';
import ApiServiceV2 from '../services/ApiServiceV2';
import { routes } from '../hooks/routes';
import { useGlobalContext } from './GlobalContext';

interface ContactFormManagementContextType {
    currentForm: ContactForm | null;
    currentFormId: string | null;
    config: ContactFormConfig;
    isLoading: boolean;
    selectForm: (formId: string) => void;
    updateConfig: (property: string, value: any) => void;
    saveCurrentForm: () => Promise<void>;
    clearSelection: () => void;
    toggleFormVisibility: (formId: string) => Promise<void>;
}

const ContactFormManagementContext = createContext<ContactFormManagementContextType | undefined>(undefined);

interface ContactFormManagementContextProviderProps {
    children: React.ReactNode;
}

// Default config for new forms or when no form is selected (moved outside component to prevent re-creation)
const defaultConfig: ContactFormConfig = {
    formFields: [],
    enableCaptcha: false,
    captchaType: 'math',
    emailSubject: 'Neue Kontaktanfrage',
    successMessage: 'Vielen Dank! Ihre Nachricht wurde erfolgreich übermittelt.',
    errorMessage: 'Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.',
    buttonText: 'Nachricht senden',
    buttonStyle: 'primary' as const,
    buttonAlign: 'left' as const,
    buttonCssClass: '',
    navButtonCssClass: '',
    inputCssClass: '',
    formCssClass: '',
    isMultiStep: false,
    steps: [
        { id: '1', title: 'Schritt 1', description: 'Grundlegende Informationen' }
    ],
    nextButtonText: 'Weiter',
    prevButtonText: 'Zurück',
    finalButtonText: 'Absenden',
    showProgress: true,
    conditionalGroups: [],
    enableConditionalLogic: false,
    // Default injection settings per form
    autoInjectEnabled: false,
    injectionSelector: '#content-wrapper',
    injectionPages: 'all'
};

export const ContactFormManagementContextProvider: React.FC<ContactFormManagementContextProviderProps> = ({ children }) => {
    const { showToast } = useGlobalContext();
    const [currentForm, setCurrentForm] = useState<ContactForm | null>(null);
    const [currentFormId, setCurrentFormId] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    
    const [config, setConfig] = useState<ContactFormConfig>(defaultConfig);

    // Load specific form by ID
    const selectForm = useCallback(async (formId: string) => {
        try {
            setIsLoading(true);
            setCurrentFormId(formId);
            
            const api = new ApiServiceV2();
            const response = await api.callApi(routes.getForm.replace(':id', formId));
            
            if (response.data?.data?.form) {
                const form = response.data.data.form;
                
                // Convert string booleans to actual booleans for proper React rendering
                const normalizedConfig = {
                    ...form.config,
                    enableCaptcha: form.config.enableCaptcha === 'true' || form.config.enableCaptcha === true,
                    isMultiStep: form.config.isMultiStep === 'true' || form.config.isMultiStep === true,
                    showProgress: form.config.showProgress === 'true' || form.config.showProgress === true,
                    enableConditionalLogic: form.config.enableConditionalLogic === 'true' || form.config.enableConditionalLogic === true,
                    autoInjectEnabled: form.config.autoInjectEnabled === 'true' || form.config.autoInjectEnabled === true,
                    // Normalize field steps to ensure they're numbers and required to boolean
                    formFields: (form.config.formFields || []).map((field: FormField) => ({
                        ...field,
                        step: typeof field.step === 'string' ? parseInt(field.step) || 1 : (field.step || 1),
                        required: typeof field.required === 'string' 
                            ? (field.required === 'true' || field.required === '1')
                            : Boolean(field.required)
                    }))
                };
                
                // Normalize the active property to ensure it's a boolean
                const normalizedActive = typeof form.active === 'string' 
                    ? (form.active === 'true' || form.active === '1')
                    : typeof form.active === 'number'
                    ? form.active === 1
                    : Boolean(form.active);
                
                setCurrentForm({...form, active: normalizedActive, config: normalizedConfig});
                setConfig(normalizedConfig);
            } else {
                showToast('top-right', 'error', 'Formular nicht gefunden');
                clearSelection();
            }
        } catch (error) {
            console.error('Failed to load form:', error);
            showToast('top-right', 'error', 'Fehler beim Laden des Formulars');
            clearSelection();
        } finally {
            setIsLoading(false);
        }
    }, [showToast]);

    // Update config properties
    const updateConfig = useCallback((property: string, value: any) => {
        setConfig(prev => ({ ...prev, [property]: value }));
    }, []);

    // Save current form
    const saveCurrentForm = useCallback(async () => {
        if (!currentFormId || !currentForm) {
            showToast('top-right', 'error', 'Kein Formular ausgewählt');
            return;
        }

        try {
            const api = new ApiServiceV2();
            await api.callApi(routes.updateForm.replace(':id', currentFormId), 'post', {
                config: config
            });

            // Update the current form state
            setCurrentForm(prev => prev ? { ...prev, config } : null);
            showToast('top-right', 'success', 'Formular erfolgreich gespeichert!');
        } catch (error) {
            console.error('Failed to save form:', error);
            showToast('top-right', 'error', 'Fehler beim Speichern des Formulars');
        }
    }, [currentFormId, currentForm, config, showToast]);

    // Clear form selection
    const clearSelection = useCallback(() => {
        setCurrentForm(null);
        setCurrentFormId(null);
        setConfig(defaultConfig);
    }, []);

    // Toggle form visibility
    const toggleFormVisibility = useCallback(async (formId: string) => {
        try {
            const api = new ApiServiceV2();
            await api.callApi(routes.toggleFormVisibility.replace(':id', formId), 'post');
            
            // Update the current form state if it's the active form
            if (currentFormId === formId && currentForm) {
                setCurrentForm(prev => prev ? { ...prev, active: !prev.active } : null);
            }
            
            showToast('top-right', 'success', 'Sichtbarkeit erfolgreich geändert!');
        } catch (error) {
            console.error('Failed to toggle visibility:', error);
            showToast('top-right', 'error', 'Fehler beim Ändern der Sichtbarkeit');
        }
    }, [currentFormId, currentForm, showToast]);

    const value: ContactFormManagementContextType = {
        currentForm,
        currentFormId,
        config,
        isLoading,
        selectForm,
        updateConfig,
        saveCurrentForm,
        clearSelection,
        toggleFormVisibility
    };

    return (
        <ContactFormManagementContext.Provider value={value}>
            {children}
        </ContactFormManagementContext.Provider>
    );
};

export const useContactFormManagement = () => {
    const context = useContext(ContactFormManagementContext);
    if (context === undefined) {
        throw new Error('useContactFormManagement must be used within a ContactFormManagementContextProvider');
    }
    return context;
};

export default ContactFormManagementContext; 